import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, MessageSquare, Mail } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';

// WhatsApp icon
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Route-specific navigation configurations
const routeNavConfig = {
  '/client': {
    en: [
      { label: 'What We Offer', href: '#about' },
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Pricing', href: '#rates' },
      { label: 'House Rules', href: '#boundaries' },
      { label: 'Request', href: '#book' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Service Area', href: '#location' },
    ],
    es: [
      { label: 'Qué Ofrecemos', href: '#about' },
      { label: 'Cómo Funciona', href: '#how-it-works' },
      { label: 'Precios', href: '#rates' },
      { label: 'Reglas', href: '#boundaries' },
      { label: 'Solicitar', href: '#book' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Área de Servicio', href: '#location' },
    ]
  },
  '/orientation': {
    en: [
      { label: 'What Casa Is', href: '#about' },
      { label: 'Brand Orientation', href: '#orientation' },
      { label: 'Expectations', href: '#expectations' },
      { label: 'Rules & Commitments', href: '#rules' },
      { label: 'Start Your Journey', href: '#apply' },
      { label: 'Employee Handbook', href: '/handbook', isRoute: true },
    ],
    es: [
      { label: 'Qué es Casa', href: '#about' },
      { label: 'Orientación de Marca', href: '#orientation' },
      { label: 'Expectativas', href: '#expectations' },
      { label: 'Reglas y Compromisos', href: '#rules' },
      { label: 'Comienza Tu Camino', href: '#apply' },
      { label: 'Manual del Empleado', href: '/handbook', isRoute: true },
    ]
  },
  '/recruit': {
    en: [
      { label: 'What Casa Is', href: '#about' },
      { label: 'Brand Orientation', href: '#orientation' },
      { label: 'Expectations', href: '#expectations' },
      { label: 'Rules & Commitments', href: '#rules' },
      { label: 'Start Your Journey', href: '#apply' },
      { label: 'Employee Handbook', href: '/handbook', isRoute: true },
    ],
    es: [
      { label: 'Qué es Casa', href: '#about' },
      { label: 'Orientación de Marca', href: '#orientation' },
      { label: 'Expectativas', href: '#expectations' },
      { label: 'Reglas y Compromisos', href: '#rules' },
      { label: 'Comienza Tu Camino', href: '#apply' },
      { label: 'Manual del Empleado', href: '/handbook', isRoute: true },
    ]
  },
  '/handbook': {
    en: [
      { label: 'Quick Start', href: '#core-ten' },
      { label: 'Sections A–J', href: '#sections' },
    ],
    es: [
      { label: 'Inicio Rápido', href: '#core-ten' },
      { label: 'Secciones A–J', href: '#sections' },
    ]
  },
  '/private': {
    en: [
      { label: 'Overview', href: '#overview' },
      { label: 'Contact', href: '#contact' },
    ],
    es: [
      { label: 'Resumen', href: '#overview' },
      { label: 'Contacto', href: '#contact' },
    ]
  }
};

export function Navbar() {
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  // Get nav links for current route
  const navLinks = routeNavConfig[location.pathname]?.[language] || [];
  
  // Don't show navbar on hub page
  const showNavbar = location.pathname !== '/';

  // Track active section on scroll
  useEffect(() => {
    if (!navLinks.length) return;

    const handleScroll = () => {
      const sections = navLinks
        .filter(link => !link.isRoute && !link.isModal)
        .map(link => link.href.replace('#', ''));
      let currentSection = '';
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            currentSection = sectionId;
          }
        }
      }
      
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navLinks, location.pathname]);

  // Close mobile menu on route change - use callback to avoid lint warning
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavClick = (link) => {
    setMobileMenuOpen(false);
    
    if (link.isRoute) {
      navigate(link.href);
      return;
    }
    
    const id = link.href.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Logo click always routes to main landing
  const handleLogoClick = () => {
    navigate('/');
  };

  if (!showNavbar) return null;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#07060B]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24">
          {/* Logo + Contact Shortcuts Column */}
          <div className="flex flex-col items-start gap-1">
            {/* Logo - 40% larger, clickable routes to home */}
            <button 
              onClick={handleLogoClick}
              className="flex items-center flex-shrink-0 group" 
              data-testid="navbar-logo"
              title={language === 'en' ? 'Home' : 'Inicio'}
            >
              <img 
                src={siteConfig.brand.logoUrl}
                alt="Casa de Candy"
                className="h-16 sm:h-20 md:h-24 w-auto object-contain animate-breathe group-hover:scale-110 transition-transform duration-300"
                style={{ 
                  filter: 'drop-shadow(0 0 10px rgba(139, 92, 255, 0.7)) drop-shadow(0 0 20px rgba(255, 77, 166, 0.4))'
                }}
              />
            </button>
            
            {/* Contact Shortcuts - Icons only, discreet */}
            <div className="hidden sm:flex items-center gap-2 ml-1">
              <a
                href={`tel:${siteConfig.contact.phone}`}
                className="p-1.5 rounded-md text-[#A7A3BA]/60 hover:text-[#27D7FF] hover:bg-[#27D7FF]/10 transition-all"
                title={language === 'en' ? 'Call' : 'Llamar'}
              >
                <Phone className="w-3.5 h-3.5" />
              </a>
              <a
                href={`sms:${siteConfig.contact.phone}`}
                className="p-1.5 rounded-md text-[#A7A3BA]/60 hover:text-[#FF4DA6] hover:bg-[#FF4DA6]/10 transition-all"
                title={language === 'en' ? 'Text' : 'Mensaje'}
              >
                <MessageSquare className="w-3.5 h-3.5" />
              </a>
              <a
                href={siteConfig.contact.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 rounded-md text-[#A7A3BA]/60 hover:text-green-400 hover:bg-green-400/10 transition-all"
                title="WhatsApp"
              >
                <WhatsAppIcon className="w-3.5 h-3.5" />
              </a>
              <a
                href={`mailto:${siteConfig.brand.email}`}
                className="p-1.5 rounded-md text-[#A7A3BA]/60 hover:text-[#8B5CFF] hover:bg-[#8B5CFF]/10 transition-all"
                title="Email"
              >
                <Mail className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Desktop Nav */}
          {navLinks.length > 0 && (
            <div className="hidden lg:flex items-center gap-1 xl:gap-2">
              {navLinks.map((link, idx) => (
                <button
                  key={idx}
                  onClick={() => handleNavClick(link)}
                  className={`px-3 py-2 text-xs xl:text-sm tracking-wide font-medium whitespace-nowrap transition-all rounded-lg ${
                    !link.isRoute && !link.isModal && activeSection === link.href.replace('#', '')
                      ? 'text-[#FF4DA6] bg-[#FF4DA6]/10'
                      : 'text-[#A7A3BA] hover:text-[#FF4DA6] hover:bg-[#FF4DA6]/5'
                  }`}
                  data-testid={`nav-link-${link.href.replace('#', '').replace('/', '')}`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border border-[#8B5CFF]/50 text-[#8B5CFF] hover:bg-[#8B5CFF]/10 transition-all"
              data-testid="language-toggle"
            >
              {language === 'en' ? 'ES' : 'EN'}
            </button>

            {/* Mobile Menu Button */}
            {navLinks.length > 0 && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-[#A7A3BA] hover:text-[#F4F2FF]"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && navLinks.length > 0 && (
        <div className="lg:hidden bg-[#0B0A12] border-t border-white/5">
          <div className="px-4 py-4 space-y-1">
            {/* Mobile contact shortcuts */}
            <div className="flex items-center gap-2 pb-3 mb-3 border-b border-white/5">
              <a href={`tel:${siteConfig.contact.phone}`} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#27D7FF]/10 text-[#27D7FF] text-xs">
                <Phone className="w-3 h-3" /> Call
              </a>
              <a href={`sms:${siteConfig.contact.phone}`} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#FF4DA6]/10 text-[#FF4DA6] text-xs">
                <MessageSquare className="w-3 h-3" /> Text
              </a>
              <a href={siteConfig.contact.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-2 rounded-lg bg-green-400/10 text-green-400 text-xs">
                <WhatsAppIcon className="w-3 h-3" /> WhatsApp
              </a>
            </div>
            
            {navLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => handleNavClick(link)}
                className={`block w-full text-left px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                  !link.isRoute && !link.isModal && activeSection === link.href.replace('#', '')
                    ? 'text-[#FF4DA6] bg-[#FF4DA6]/10'
                    : 'text-[#A7A3BA] hover:text-[#FF4DA6] hover:bg-[#FF4DA6]/5'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
